import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { format, addDays, subDays } from "date-fns";
import { useBooking } from "../contexts/BookingContext";

/**
 * Persistent Event Date Bar — inline within content, below filter tabs.
 * Supports ?highlight=date to flash the bar when linked from homepage.
 */
export default function PersistentDateBar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [flash, setFlash] = useState(false);
  const { 
    eventDate, 
    rentalDuration, 
    hasDateSelected,
    openDateModal,
    dispatchBuffer,
  } = useBooking();

  useEffect(() => {
    if (searchParams.get("highlight") === "date") {
      setFlash(true);
      searchParams.delete("highlight");
      setSearchParams(searchParams, { replace: true });
      const timer = setTimeout(() => setFlash(false), 2500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Delivery = Event - 1, Rental End = Delivery + Duration - 1
  const deliveryDate = eventDate ? subDays(eventDate, 1) : null;
  const rentalEnd = deliveryDate && rentalDuration ? addDays(deliveryDate, rentalDuration - 1) : null;

  const handleOpenModal = () => openDateModal();

  if (!hasDateSelected) {
    return (
      <div className={`w-full py-1.5 sm:py-5 flex flex-col items-center transition-all duration-500 ${flash ? 'bg-[#9A7B4F]/10 ring-2 ring-[#9A7B4F]/30 rounded' : ''}`} data-testid="persistent-date-bar">
        <div className="flex items-center gap-1 sm:gap-1.5 justify-center">
          <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#9A7B4F] flex-shrink-0" strokeWidth={1.5} />
          <span className="text-[11px] sm:text-[15px] text-[#1A1A1A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Select your event date to see available pieces
          </span>
        </div>
        <button
          onClick={handleOpenModal}
          className="text-[11px] sm:text-[15px] text-[#9A7B4F] hover:text-[#8a6b3f] transition-colors font-medium mt-0.5 sm:mt-1"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          data-testid="select-date-btn"
        >
          Select Date
        </button>
      </div>
    );
  }

  return (
    <div className={`w-full py-5 flex justify-center transition-all duration-500 ${flash ? 'bg-[#9A7B4F]/10 ring-2 ring-[#9A7B4F]/30 rounded' : ''}`} data-testid="persistent-date-bar">
      <div className="flex items-center gap-1 flex-wrap justify-center">
        <CalendarDays className="w-4 h-4 text-[#9A7B4F] mr-1" strokeWidth={1.5} />
        
        <span className="text-[15px] text-[#1A1A1A] font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Your event date:
        </span>
        <span className="text-[15px] text-[#1A1A1A] font-medium ml-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {format(eventDate, "d MMMM yyyy")}
        </span>

        {rentalDuration && rentalEnd && (
          <>
            <span className="text-[#9A7B4F] mx-2">·</span>
            <span className="text-[15px] text-[#1A1A1A] font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {rentalDuration}-Day Rental
            </span>
            <span className="text-[#9A7B4F] mx-2">·</span>
            <span className="text-[15px] text-[#1A1A1A] font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Returns {format(rentalEnd, "d MMMM")}
            </span>
          </>
        )}

        <span className="text-[#9A7B4F] mx-2">·</span>
        <button
          onClick={handleOpenModal}
          className="text-[15px] text-[#9A7B4F] hover:text-[#8a6b3f] transition-colors font-medium"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
          data-testid="change-date-btn"
        >
          Change
        </button>
      </div>
    </div>
  );
}
